import React, { useEffect, useRef } from "react";
import "./Popover.sass";

const MODAL_SELECTOR = '[aria-modal="true"]';

/**
 * Lightweight popover — opens below the trigger, closes on outside click and Esc.
 * No portal: positioned relative to the nearest positioned ancestor.
 * Focus trap is not required (TV-04 spec).
 *
 * @param {object} props
 * @param {React.ReactNode} props.trigger - the anchor element (button etc.)
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {React.ReactNode} props.children - popover content
 * @param {string} [props.className] - extra class for the panel
 */
const Popover = ({ trigger, isOpen, onClose, children, className = "" }) => {
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    // A pointer press, not mousedown: a finger that drags outside sends no mouse events
    const handlePointerDown = (event) => {
      // A modal opened from the popover lives outside of it, so clicks
      // inside that modal must not close the popover.
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        !event.target.closest(MODAL_SELECTOR)
      ) {
        onClose();
      }
    };

    // Clicks inside an iframe (e.g. the TradingView chart) never reach
    // document, but the parent window loses focus to that iframe.
    const handleWindowBlur = () => {
      if (document.activeElement?.tagName === "IFRAME") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [isOpen, onClose]);

  return (
    <div className="popover" ref={wrapperRef}>
      {trigger}
      {isOpen && (
        <div className={`popover__panel${className ? ` ${className}` : ""}`}>
          {children}
        </div>
      )}
    </div>
  );
};

export default Popover;
